import { useId } from 'react';
import type { CSSProperties } from 'react';

import { css, cx, keyframes } from '@emotion/css';

/** Colores del puerto al atardecer (los de la web y la maqueta de entrada). */
export const ATARDECER = {
  cielo1: '#f6dfb8',
  cielo2: '#eebf8f',
  cielo3: '#f3e6cf',
  papel: '#fbf6ec',
  papel2: '#f3ead8',
  tinta: '#13232b',
  tinta2: '#4b5d66',
  tinta3: '#6b787e',
  linea: '#e3d6bd',
  laton: '#c49a4c',
  latonOscuro: '#7d5f24',
  verde: '#1f8f5c',
  rojo: '#c4513f',
  icono: '#0f3b3a',
} as const;

/** El cielo cálido que va detrás de la entrada y de las cargas. */
export const CIELO = `linear-gradient(180deg, ${ATARDECER.cielo1} 0%, ${ATARDECER.cielo2} 46%, ${ATARDECER.cielo3} 100%)`;

type Variante =
  /** La entrada: sol, faro, velero que se mece y Gavi en su noray. */
  | 'entrada'
  /** Lista de espera: el muelle lleno de barcos y Gavi a un lado. */
  | 'lleno'
  /** Ya hay sitio: un barco llega al muelle y queda amarrado. */
  | 'amarre';

/**
 * El puerto de Llafranc al atardecer, la escena de arriba de la entrada y de
 * la lista de espera. Dibujo plano en SVG; con «reducir movimiento», quieto.
 */
export function PuertoAtardecer({
  variante = 'entrada',
  style,
}: {
  variante?: Variante;
  style?: CSSProperties;
}) {
  const id = useId().replace(/:/g, '');
  const agua = `agua-${id}`;
  const sol = `sol-${id}`;
  const haz = `haz-${id}`;
  return (
    <svg
      viewBox="0 -40 360 340"
      preserveAspectRatio="xMidYMax meet"
      aria-hidden
      className={estilos.escena}
      style={{ display: 'block', width: '100%', ...style }}
    >
      <defs>
        <linearGradient id={agua} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#2f5d63" />
          <stop offset=".55" stopColor="#1d3f47" />
          <stop offset="1" stopColor="#142e35" />
        </linearGradient>
        <radialGradient id={sol} cx="50%" cy="50%" r="50%">
          <stop offset="0" stopColor="#fff3d6" />
          <stop offset=".35" stopColor="#ffd99a" stopOpacity=".65" />
          <stop offset="1" stopColor="#ffd99a" stopOpacity="0" />
        </radialGradient>
        <linearGradient id={haz} x1="1" y1="0" x2="0" y2="0">
          <stop offset="0" stopColor="#fff2c4" stopOpacity=".85" />
          <stop offset="1" stopColor="#fff2c4" stopOpacity="0" />
        </linearGradient>
      </defs>

      <circle
        className={estilos.sol}
        cx={variante === 'amarre' ? 250 : 150}
        cy="92"
        r="110"
        fill={`url(#${sol})`}
      />
      <circle
        cx={variante === 'amarre' ? 250 : 150}
        cy="92"
        r="19"
        fill="#fff1cf"
        opacity=".9"
      />

      {variante === 'entrada' && (
        <>
          <g className={estilos.haz}>
            <polygon points="318,-12 40,-76 40,54" fill={`url(#${haz})`} />
          </g>
          <path
            d="M0 104 C40 84 80 74 120 86 C140 92 160 96 180 100 L0 100 Z"
            fill="#6f8a90"
            opacity=".7"
          />
          <path
            d="M250 102 C280 64 300 24 320 24 C340 28 355 54 360 74 L360 104 L250 104 Z"
            fill="#8ea3a8"
          />
          <Faro />
          <g className={estilos.vuela}>
            <g transform="translate(0,40)">
              <path
                className={estilos.ala}
                d="M0 0 Q6 -6 12 0 Q18 -6 24 0"
                fill="none"
                stroke="#3a4a52"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </g>
          </g>
        </>
      )}

      <rect x="-10" y="100" width="380" height="220" fill={`url(#${agua})`} />
      <Ola y={124} className={estilos.olaLenta} opacidad={0.35} />

      {variante === 'entrada' && (
        <>
          <g className={estilos.velero}>
            <Velero x={190} y={74} ancho={60} />
          </g>
          <Ola y={174} className={estilos.ola} opacidad={0.5} grosor={2} />
          <Muelle x={20} y={204} ancho={170} pilotes={[34, 104, 170]} />
          <Noray x={128} y={188} />
          <GaviPosada x={112} y={138} />
        </>
      )}

      {variante === 'lleno' && (
        <>
          {[138, 178, 222, 262, 306].map((x, i) => (
            <Velero key={x} x={x} y={i % 2 ? 70 : 74} ancho={i % 2 ? 40 : 36} />
          ))}
          <rect x="130" y="120" width="240" height="8" rx="3" fill="#3a2a1d" />
          <Ola y={160} className={estilos.ola} opacidad={0.5} grosor={2} />
          <Muelle x={40} y={190} ancho={110} pilotes={[52, 130]} />
          <GaviPosada x={64} y={140} />
        </>
      )}

      {variante === 'amarre' && (
        <>
          <Muelle x={20} y={176} ancho={320} pilotes={[36, 176, 316]} />
          <g className={estilos.llega}>
            <g className={estilos.velero}>
              <Velero x={120} y={72} ancho={76} />
            </g>
          </g>
          <Noray x={226} y={160} />
          <path
            className={estilos.cuerda}
            d="M186 154 Q210 176 232 162"
            fill="none"
            stroke="#e8cf96"
            strokeWidth="3"
            strokeLinecap="round"
          />
          <GaviPosada x={250} y={126} />
          <Ola y={220} className={estilos.ola} opacidad={0.5} grosor={2} />
        </>
      )}
    </svg>
  );
}

function Ola({
  y,
  className,
  opacidad,
  grosor = 1.6,
}: {
  y: number;
  className: string;
  opacidad: number;
  grosor?: number;
}) {
  return (
    <g className={className} opacity={opacidad}>
      <path
        d={`M0 ${y} ${'q25 -6 50 0 t50 0 '.repeat(6)}`}
        fill="none"
        stroke="#e6f0ee"
        strokeWidth={grosor}
      />
    </g>
  );
}

function Velero({ x, y, ancho }: { x: number; y: number; ancho: number }) {
  const s = ancho / 100;
  return (
    <g transform={`translate(${x} ${y}) scale(${s})`}>
      <path d="M0 102 L100 102 L84 120 L16 120 Z" fill="#f3ede0" />
      <path d="M16 120 L84 120 L80 124 L20 124 Z" fill={ATARDECER.rojo} />
      <line x1="48" y1="102" x2="48" y2="0" stroke="#2b3a42" strokeWidth="3" />
      <path d="M51 4 L51 96 L96 96 Z" fill="#fbf6ec" stroke="#e3d6bd" />
      <path d="M45 14 L45 96 L10 96 Z" fill="#e8cf96" />
      <path d="M48 0 L66 5 L48 10 Z" fill={ATARDECER.verde} />
    </g>
  );
}

function Faro() {
  return (
    <g>
      <polygon points="308,44 328,44 325,4 311,4" fill="#f5efe4" />
      <rect x="310" y="16" width="16" height="6" fill={ATARDECER.rojo} />
      <rect x="309" y="30" width="18" height="6" fill={ATARDECER.rojo} />
      <rect x="310" y="-12" width="16" height="14" rx="2" fill="#ffe7a6" />
      <path d="M306 -12 L318 -24 L330 -12 Z" fill="#2b3a42" />
      <rect x="306" y="1" width="24" height="3" fill="#2b3a42" />
    </g>
  );
}

function Muelle({
  x,
  y,
  ancho,
  pilotes,
}: {
  x: number;
  y: number;
  ancho: number;
  pilotes: number[];
}) {
  return (
    <g>
      <rect x={x} y={y} width={ancho} height="10" rx="3" fill="#3a2a1d" />
      {pilotes.map(p => (
        <rect key={p} x={p} y={y + 10} width="9" height="120" fill="#2a1e15" />
      ))}
    </g>
  );
}

function Noray({ x, y }: { x: number; y: number }) {
  return (
    <g>
      <rect x={x} y={y} width="18" height="18" rx="4" fill="#26343b" />
      <rect x={x - 5} y={y - 4} width="28" height="7" rx="3.5" fill="#33454d" />
    </g>
  );
}

/** Gavi posada, con el mismo dibujo que en el resto de Rumbo. */
function GaviPosada({ x, y }: { x: number; y: number }) {
  return (
    <g className={estilos.gavi}>
      <g transform={`translate(${x} ${y}) scale(0.8)`}>
        <path d="M8 40 L1 44 L10 45 Z" fill="#9fadc2" />
        <ellipse
          cx="28"
          cy="40"
          rx="19"
          ry="13"
          fill="#ffffff"
          stroke="#cdd5de"
        />
        <g fill="#ffffff" stroke="#cdd5de">
          <path d="M14 30 L12 22 L19 29 Z" />
          <path d="M22 27 L22 18 L27 27 Z" />
          <path d="M32 28 L35 20 L36 29 Z" />
        </g>
        <path
          d="M26 53 L24 59 M33 53 L35 59"
          stroke="#e8a93b"
          strokeWidth="2.6"
          strokeLinecap="round"
        />
        <circle cx="42" cy="24" r="11" fill="#ffffff" stroke="#cdd5de" />
        <g className={estilos.ojo}>
          <circle cx="45.5" cy="21.5" r="2.3" fill="#1d2733" />
          <circle cx="46.2" cy="20.8" r=".7" fill="#ffffff" />
        </g>
        <path d="M51 25 L61 27.5 L51 30 Z" fill="#e8a93b" />
        <path d="M12 38 Q22 25 40 33 Q31 38 21 47 Z" fill="#9fadc2" />
        <path d="M12 38 L8 34 L16 35 Z" fill="#59657a" />
      </g>
    </g>
  );
}

const ola = keyframes({
  from: { transform: 'translateX(0)' },
  to: { transform: 'translateX(-100px)' },
});
const cabeceo = keyframes({
  '0%, 100%': { transform: 'translate(0, 0) rotate(-2deg)' },
  '50%': { transform: 'translate(0, 3px) rotate(2deg)' },
});
const barrido = keyframes({
  '0%': { transform: 'rotate(-16deg)', opacity: 0 },
  '15%': { opacity: 0.9 },
  '50%': { transform: 'rotate(10deg)', opacity: 0.9 },
  '70%': { opacity: 0 },
  '100%': { transform: 'rotate(-16deg)', opacity: 0 },
});
const vuelo = keyframes({
  from: { transform: 'translate(-60px, 20px)' },
  to: { transform: 'translate(420px, -10px)' },
});
const aleteo = keyframes({
  '0%, 100%': { transform: 'scaleY(1)' },
  '50%': { transform: 'scaleY(-0.6)' },
});
const balanceo = keyframes({
  '0%, 100%': { transform: 'rotate(-3deg)' },
  '50%': { transform: 'rotate(3deg)' },
});
const parpadeo = keyframes({
  '0%, 92%, 100%': { transform: 'scaleY(1)' },
  '95%': { transform: 'scaleY(0.1)' },
});
const brillo = keyframes({
  '0%, 100%': { opacity: 0.55 },
  '50%': { opacity: 0.85 },
});
const llegada = keyframes({
  from: { transform: 'translateX(-170px)' },
  to: { transform: 'none' },
});
const dibuja = keyframes({ to: { strokeDashoffset: 0 } });

const estilos = {
  escena: css({
    '@media (prefers-reduced-motion: reduce)': {
      '& *': { animation: 'none !important' },
      '& .cuerda': { strokeDashoffset: 0 },
    },
  }),
  ola: css({ animation: `${ola} 9s linear infinite` }),
  olaLenta: css({ animation: `${ola} 14s linear infinite` }),
  velero: css({
    transformBox: 'fill-box',
    transformOrigin: '50% 90%',
    animation: `${cabeceo} 5s ease-in-out infinite`,
  }),
  haz: css({
    transformBox: 'view-box',
    transformOrigin: '318px -12px',
    animation: `${barrido} 8s ease-in-out infinite`,
  }),
  vuela: css({ animation: `${vuelo} 11s linear infinite` }),
  ala: css({
    transformBox: 'fill-box',
    transformOrigin: '50% 100%',
    animation: `${aleteo} .5s ease-in-out infinite`,
  }),
  gavi: css({
    transformBox: 'fill-box',
    transformOrigin: '50% 100%',
    animation: `${balanceo} 3.2s ease-in-out infinite`,
  }),
  ojo: css({
    transformBox: 'fill-box',
    transformOrigin: 'center',
    animation: `${parpadeo} 4.5s infinite`,
  }),
  sol: css({ animation: `${brillo} 6s ease-in-out infinite` }),
  llega: css({
    animation: `${llegada} 1.4s cubic-bezier(.2,.8,.2,1) .1s both`,
  }),
  cuerda: cx(
    'cuerda',
    css({
      strokeDasharray: 120,
      strokeDashoffset: 120,
      animation: `${dibuja} 1s ease-out 1.2s both`,
    }),
  ),
};
